// All product time is LOCKED to Queenstown (Pacific/Auckland) — never the
// viewer's or server's locale. Staff may be anywhere (e.g. Colombia) and
// Vercel runs in UTC; every "today", day-window, and clock-position
// computation must go through these helpers. DST-aware via Intl
// (NZST +12:00 in winter, NZDT +13:00 in summer — never hardcode either).

export const NZ_TZ = "Pacific/Auckland";

/** Today's calendar date in Queenstown, as YYYY-MM-DD. */
export function todayNZ(): string {
  return new Date().toLocaleDateString("en-CA", { timeZone: NZ_TZ });
}

/** The UTC offset ("+12:00" / "+13:00") in force in NZ on a given calendar date. */
export function nzOffset(dateStr: string): string {
  // Noon UTC sits safely inside the same NZ calendar day for any offset.
  const probe = new Date(`${dateStr}T12:00:00Z`);
  // Derive the offset by diffing "NZ wall clock read as UTC" against the true
  // UTC instant. Uses only numeric date/time parts — the oldest, universally
  // supported Intl surface — so it never touches `timeZoneName: "longOffset"`,
  // which some browser/OS ICU builds reject (constructor throws RangeError).
  const parts = new Intl.DateTimeFormat("en-US", {
    timeZone: NZ_TZ, hour12: false,
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", second: "2-digit",
  }).formatToParts(probe);
  const get = (t: string) => Number(parts.find((p) => p.type === t)?.value ?? 0);
  const asUTC = Date.UTC(get("year"), get("month") - 1, get("day"), get("hour"), get("minute"), get("second"));
  const diffMin = Math.round((asUTC - probe.getTime()) / 60000);
  const sign = diffMin >= 0 ? "+" : "-";
  const abs = Math.abs(diffMin);
  return `${sign}${String(Math.floor(abs / 60)).padStart(2, "0")}:${String(abs % 60).padStart(2, "0")}`;
}

/** ISO instant for midnight (start of day) in NZ on the given date. */
export function nzDayStart(dateStr: string): string {
  return `${dateStr}T00:00:00${nzOffset(dateStr)}`;
}

/** ISO instant for end of day (23:59:59.999) in NZ on the given date. */
export function nzDayEnd(dateStr: string): string {
  return `${dateStr}T23:59:59.999${nzOffset(dateStr)}`;
}

/** A timestamp's wall-clock hour + minute in NZ (e.g. for timeline Y positions). */
export function nzTimeParts(iso: string | Date): { h: number; m: number } {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  const parts = new Intl.DateTimeFormat("en-NZ", {
    timeZone: NZ_TZ, hour: "numeric", minute: "numeric", hour12: false,
  }).formatToParts(d);
  const h = Number(parts.find((p) => p.type === "hour")?.value ?? 0);
  const m = Number(parts.find((p) => p.type === "minute")?.value ?? 0);
  return { h: h === 24 ? 0 : h, m };
}

/** The NZ calendar date (YYYY-MM-DD) a timestamp falls on. */
export function nzDateOf(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return d.toLocaleDateString("en-CA", { timeZone: NZ_TZ });
}

/** True only for a real YYYY-MM-DD calendar date in years 2000–2099 ("2026-02-30",
 *  "abc", "" → false). Gate untrusted dates (URL params, typed inputs) with this before
 *  date math — addDaysNZ / addDays throw "RangeError: Invalid time value" on a bad
 *  string. The year window is deliberate: Date.UTC remaps years 0–99 to 1900s (nzOffset
 *  then produces garbage and fmtNZDate throws) and 9999-12-31 + 1 day isn't a YMD. */
export function isYmd(s: unknown): s is string {
  if (typeof s !== "string" || !/^20\d{2}-\d{2}-\d{2}$/.test(s)) return false;
  const d = new Date(`${s}T12:00:00Z`);
  return !Number.isNaN(d.getTime()) && d.toISOString().slice(0, 10) === s;
}

/** Add n days to a YYYY-MM-DD calendar date (pure date math, no tz pitfalls). */
export function addDaysNZ(dateStr: string, n: number): string {
  const d = new Date(`${dateStr}T12:00:00Z`); // noon UTC: immune to DST shifts
  d.setUTCDate(d.getUTCDate() + n);
  return d.toISOString().slice(0, 10);
}

/** Format a timestamp's time-of-day in NZ, e.g. "2:30 pm". */
export function fmtNZTime(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  return d.toLocaleTimeString("en-NZ", { hour: "numeric", minute: "2-digit", timeZone: NZ_TZ });
}

/** Format a YYYY-MM-DD calendar date for display (NZ wall date, no tz drift). */
export function fmtNZDate(dateStr: string, opts: Intl.DateTimeFormatOptions = { weekday: "long", day: "numeric", month: "long", year: "numeric" }): string {
  // Guard a missing/malformed date: an Invalid Date + a `weekday` opt makes
  // toLocaleDateString throw "RangeError: Invalid time value". Render "" instead.
  if (!dateStr || Number.isNaN(new Date(`${dateStr}T12:00:00Z`).getTime())) return "";
  return new Date(`${dateStr}T12:00:00${nzOffset(dateStr)}`).toLocaleDateString("en-NZ", { ...opts, timeZone: NZ_TZ });
}
